<script setup lang="ts">
import IconClose from '@/components/icons/IconClose.vue'
import type {
  StepDeliveryFormErrorManager,
  StepDeliveryFormErrorPath,
  StepDeliveryStepFormType,
} from './formSchema'
import StepDeliveryFormError from '@/components/organisms/StepDeliveryForm/StepDeliveryFormError.vue'
import DolphinTextarea from '@/components/atoms/DolphinTextarea.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import StepDeliveryStepAnswerSelectionForm from '@/components/organisms/StepDeliveryForm/StepDeliveryStepAnswerSelectionForm.vue'

const stepModel = defineModel<StepDeliveryStepFormType>({ required: true })

const props = defineProps<{
  index: number
  shownControl: boolean
  formErrorManager: StepDeliveryFormErrorManager
}>()

const emit = defineEmits<{
  (event: 'delete'): void
}>()

const handleClickDeleteButton = () => {
  if (window.confirm('本当に削除しますか？')) {
    emit('delete')
  }
}
</script>

<template>
  <section :class="$style.question">
    <header :class="$style.header">
      <p :class="$style.heading">ステップ{{ props.index + 1 }}</p>
      <div v-if="props.shownControl" :class="$style.controls">
        <button @click="handleClickDeleteButton" :class="$style.buttonDelete">
          <IconClose />
        </button>
      </div>
    </header>
    <input type="hidden" v-model="stepModel.guid" />
    <div :class="$style.formRow">
      <p :class="$style.label">対象日</p>
      <label :class="$style.isAnswerRequired">
        <DolphinSelector
          :class="$style.plannerSelector__select"
          :options="
            Array.from({ length: 60 }, (_, i) => ({
              text: String(i + 1),
              value: i + 1,
            }))
          "
          v-model="stepModel.targetDayAfter"
        />
        <span>日後</span>
        <StepDeliveryFormError
          v-if="
            formErrorManager.get(
              `steps.${index}.targetDayAfter` as StepDeliveryFormErrorPath
            )
          "
        >
          {{
            formErrorManager.get(
              `steps.${index}.targetDayAfter` as StepDeliveryFormErrorPath
            )
          }}
        </StepDeliveryFormError>
      </label>
    </div>
    <div :class="$style.formRow">
      <p :class="$style.label">配信時間</p>
      <label :class="$style.isAnswerRequired">
        <DolphinSelector
          :class="$style.plannerSelector__select"
          :options="
            Array.from({ length: 23 }, (_, i) => ({
              text: String(i),
              value: i,
            }))
          "
          v-model="stepModel.targetHour"
        />
        <span>時</span>
        <StepDeliveryFormError
          v-if="
            formErrorManager.get(
              `steps.${index}.targetHour` as StepDeliveryFormErrorPath
            )
          "
          >{{
            formErrorManager.get(
              `steps.${index}.targetHour` as StepDeliveryFormErrorPath
            )
          }}
        </StepDeliveryFormError>
      </label>
    </div>
    <div :class="$style.formRow">
      <p :class="$style.label">メッセージ</p>
      <DolphinTextarea v-model="stepModel.message" :rows="8" />
      <StepDeliveryFormError
        v-if="
          formErrorManager.get(
            `steps.${index}.message` as StepDeliveryFormErrorPath
          )
        "
        >{{
          formErrorManager.get(
            `steps.${index}.message` as StepDeliveryFormErrorPath
          )
        }}
      </StepDeliveryFormError>
    </div>
    <StepDeliveryStepAnswerSelectionForm
      v-model="stepModel.answerSelections"
      :stepIndex="index"
      :form-error-manager="formErrorManager"
    />
  </section>
</template>

<style lang="scss" module>
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.heading {
  font-size: 16px;
}

.controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.question {
  display: flex;
  flex-direction: column;
  gap: 24px;
  border: 1px solid $color-surface-tertiary;
  background-color: $color-surface-secondary;
  border-radius: 4px;
  padding: 16px;
  margin-top: 16px;
}

.formRow {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  color: $color-text-and-object-high-emphasis;
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
}

.isAnswerRequired {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.buttonMovePrevious {
  font-size: 18px;
  line-height: 18px;
  rotate: 270deg;

  svg {
    margin-right: 4px;
  }
}

.buttonMoveNext {
  font-size: 18px;
  line-height: 18px;
  rotate: 90deg;

  svg {
    margin-left: 4px;
  }
}

.buttonDelete {
  font-size: 24px;
  line-height: 24px;

  svg {
    margin-top: 4px;
  }
}

.options {
  display: block;
  list-style-type: disc;
  margin-block-start: 8px;
  padding-inline-start: 24px;
}

.option {
  list-style: disc;
  line-height: 1.8;
}
</style>
